import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { SITE_URL } from '../../config';
import { db } from '../../firebase';
import { stripMarkdown } from '../../utils/stripMarkdown';

function safeText(value) {
  return String(value || '').trim();
}

function buildExcerpt(body, max = 220) {
  const plain = stripMarkdown(body);
  if (!plain) return '';
  return plain.length > max ? `${plain.slice(0, max).trim()}…` : plain;
}

function SlotReviewsPage() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'slotReviews'), orderBy('createdAt', 'desc'), limit(100));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setItems(next);
        setLoading(false);
      },
      () => {
        setItems([]);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const canonicalUrl = `${SITE_URL}${location.pathname}`;

  return (
    <section aria-label="Slot reviews">
      <Helmet>
        <title>Slot reviews – IGAMINGSPOT</title>
        <meta name="description" content="Latest iGaming slot reviews: concise, structured breakdowns and gameplay notes." />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Slot reviews – IGAMINGSPOT" />
        <meta
          property="og:description"
          content="Latest iGaming slot reviews: concise, structured breakdowns and gameplay notes."
        />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={`${SITE_URL}/logo512.png`} />
        <meta property="og:site_name" content="IGAMINGSPOT" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Slot reviews – IGAMINGSPOT" />
        <meta
          name="twitter:description"
          content="Latest iGaming slot reviews: concise, structured breakdowns and gameplay notes."
        />
        <meta name="twitter:image" content={`${SITE_URL}/logo512.png`} />
      </Helmet>

      <div className="content-card">
        <div className="content-card-header">
          <div>
            <div className="section-title">Slots review</div>
            <h1 className="section-subtitle">All slot reviews</h1>
          </div>
          <div className="secondary-text">{items.length} items</div>
        </div>

        {loading && <p className="secondary-text">Loading slot reviews…</p>}
        {!loading && !items.length && <p className="secondary-text">No slot reviews yet.</p>}

        <div className="slot-reviews-list">
          {items.map((item) => {
            const title = safeText(item.title) || 'Slot review';
            const provider = safeText(item.provider);
            const thumb = safeText(item.imageUrl);
            const href = `/slots/${item.slug || item.id}`;
            const excerpt = item.body ? buildExcerpt(item.body, 220) : '';

            return (
              <Link key={item.id} to={href} className="slot-review-row">
                <div className="slot-review-row-thumb" aria-hidden="true">
                    {thumb ? <img src={thumb} alt="" loading="lazy" /> : null}
                  </div>
                <div className="slot-review-row-main">
                  <div className="slot-review-row-title">{title}</div>
                  <div className="secondary-text">{provider || '—'}</div>
                  {excerpt && <div className="slot-review-row-excerpt">{excerpt}</div>}
                  </div>
                <div className="slot-review-row-open">Open →</div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default SlotReviewsPage;


